import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  DOTYKACKA_PAYMENT_METHODS,
  DotykackaBranch,
  DotykackaService,
  DotykackaStatus,
  submitConnectForm,
} from '../../services/dotykacka/dotykackaService';

const dotykackaService = new DotykackaService();

const buttonSx = {
  borderRadius: 'var(--radius-sm)',
  boxShadow: 'none',
  textTransform: 'none',
} as const;

interface DotykackaSectionProps {
  locationId: string;
  locationName?: string;
}

/** Connects a location to its Dotykačka till and picks the branch that receives orders. */
export const DotykackaSection: React.FC<DotykackaSectionProps> = ({ locationId, locationName }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const navigate = useNavigate();

  const [status, setStatus] = React.useState<DotykackaStatus | null>(null);
  const [branches, setBranches] = React.useState<DotykackaBranch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = React.useState('');
  const [busy, setBusy] = React.useState<'connect' | 'branch' | 'payment' | 'disconnect' | null>(null);
  const [loading, setLoading] = React.useState(true);

  const notifyError = React.useCallback((error: any, fallbackKey: string) => {
    Sentry.captureException(error);
    notify(error?.response?.data?.error ?? t(fallbackKey), { type: 'error' });
  }, [notify, t]);

  // A ref, so a new notify/t identity (e.g. once i18n finishes loading) does not refetch from Dotypos.
  const notifyErrorRef = React.useRef(notifyError);
  notifyErrorRef.current = notifyError;

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const nextStatus = await dotykackaService.getStatus(locationId);
      setStatus(nextStatus);
      setSelectedBranchId(nextStatus.branchId ?? '');

      if (nextStatus.connected && nextStatus.reachable) {
        setBranches(await dotykackaService.getBranches(locationId));
      } else {
        setBranches([]);
      }
    } catch (error) {
      notifyErrorRef.current(error, 'dotykacka.errors.load');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleConnect = async () => {
    setBusy('connect');
    try {
      submitConnectForm(await dotykackaService.createConnectForm(locationId));
    } catch (error) {
      notifyError(error, 'dotykacka.errors.connect');
      setBusy(null);
    }
  };

  const handleSaveBranch = async () => {
    setBusy('branch');
    try {
      setStatus(await dotykackaService.selectBranch(locationId, selectedBranchId));
      notify(t('dotykacka.branchSaved'), { type: 'success' });
    } catch (error) {
      notifyError(error, 'dotykacka.errors.branch');
    } finally {
      setBusy(null);
    }
  };

  const handlePaymentMethodChange = async (value: string) => {
    setBusy('payment');
    try {
      setStatus(await dotykackaService.selectPaymentMethod(locationId, value === '' ? null : Number(value)));
      notify(t('dotykacka.paymentMethodSaved'), { type: 'success' });
    } catch (error) {
      notifyError(error, 'dotykacka.errors.paymentMethod');
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('dotykacka.disconnectConfirm'))) {
      return;
    }

    setBusy('disconnect');
    try {
      await dotykackaService.disconnect(locationId);
      notify(t('dotykacka.disconnected'), { type: 'info' });
      await load();
    } catch (error) {
      notifyError(error, 'dotykacka.errors.disconnect');
    } finally {
      setBusy(null);
    }
  };

  const connectButton = (label: string) => (
    <Button
      variant="contained"
      onClick={handleConnect}
      disabled={busy !== null}
      sx={{
        ...buttonSx,
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-text-inverse)',
        '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
      }}
    >
      {busy === 'connect' ? <CircularProgress size={20} sx={{ color: 'white' }} /> : label}
    </Button>
  );

  return (
    <Box
      sx={{
        mt: 'var(--space-6)',
        p: 'var(--space-5)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--color-surface)',
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-lg)', color: 'var(--color-text-primary)' }}>
        {t('dotykacka.title')}
      </Typography>
      <Typography sx={{ mt: 'var(--space-1)', mb: 'var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        {t('dotykacka.description')}
      </Typography>

      {loading && <CircularProgress size={24} />}

      {!loading && status && !status.connected && connectButton(t('dotykacka.connect'))}

      {!loading && status?.connected && (
        <Stack spacing="var(--space-4)">
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            {t('dotykacka.connectedToCloud', { cloudId: status.cloudId })}
          </Typography>

          {status.reachable === false && (
            <Alert severity="warning" action={connectButton(t('dotykacka.reconnect'))}>
              {t('dotykacka.unreachable')}
            </Alert>
          )}

          {status.reachable && !status.branchId && (
            <Alert severity="info">{t('dotykacka.chooseBranchHint')}</Alert>
          )}

          {status.reachable && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing="var(--space-3)" alignItems={{ sm: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="dotykacka-branch-label">{t('dotykacka.branch')}</InputLabel>
                <Select
                  labelId="dotykacka-branch-label"
                  label={t('dotykacka.branch')}
                  value={selectedBranchId}
                  onChange={(event) => setSelectedBranchId(event.target.value)}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={handleSaveBranch}
                disabled={busy !== null || !selectedBranchId || selectedBranchId === status.branchId}
                sx={buttonSx}
              >
                {busy === 'branch' ? <CircularProgress size={20} /> : t('dotykacka.saveBranch')}
              </Button>
            </Stack>
          )}

          <Box>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="dotykacka-payment-label">{t('dotykacka.receipt')}</InputLabel>
              <Select
                labelId="dotykacka-payment-label"
                label={t('dotykacka.receipt')}
                value={status.paymentMethodId == null ? '' : String(status.paymentMethodId)}
                disabled={busy !== null}
                onChange={(event) => handlePaymentMethodChange(event.target.value)}
              >
                <MenuItem value="">{t('dotykacka.paymentMethods.none')}</MenuItem>
                {DOTYKACKA_PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method.id} value={String(method.id)}>{t(method.labelKey)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ mt: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
              {t(status.paymentMethodId == null ? 'dotykacka.receiptHintOff' : 'dotykacka.receiptHintOn')}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing="var(--space-3)">
            <Button
              variant="outlined"
              disabled={busy !== null || !status.reachable}
              onClick={() => navigate(`/locationsManagement/${locationId}/dotykacka`, { state: { locationName } })}
              sx={buttonSx}
            >
              {t('dotykacka.mapProducts')}
            </Button>
            <Button
              variant="text"
              color="error"
              disabled={busy !== null}
              onClick={handleDisconnect}
              sx={buttonSx}
            >
              {busy === 'disconnect' ? <CircularProgress size={20} /> : t('dotykacka.disconnect')}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
